import { Component, OnInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { PaginatedResponse, PaginationInfo } from 'src/app/shared/models';
import { VehicleTypeService } from 'src/app/services/vehicle-type/vehicle-type.service';
import { VehicleTypeModalService } from 'src/app/services/vehicle-type-modal/vehicle-type-modal.service';
import Swal from 'sweetalert2';
import { forkJoin } from 'rxjs';

declare var bootstrap: any;

@Component({
  selector: 'app-vehicle-types',
  templateUrl: './vehicle-types.component.html',
  styleUrls: ['./vehicle-types.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ height: '0px', opacity: 0 }),
        animate('300ms ease-in', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-out', style({ height: '0px', opacity: 0 }))
      ])
    ])
  ]
})
export class VehicleTypesComponent extends DestructorComponent implements OnInit, AfterViewInit {

  // Data properties
  vehicleTypes: any[] = [];
  isLoading = false;

  // Pagination
  currentPage = 1;
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // Search and filters
  searchTerm = '';
  showFiltersForm = false;
  filters: Record<string, any> = {};

  // Bulk selection
  selectedVehicleTypes: string[] = [];

  constructor(
    private _vehicleTypeService: VehicleTypeService,
    private _vehicleTypeModalService: VehicleTypeModalService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadVehicleTypes();
  }

  ngAfterViewInit(): void {
    // No longer needed since we removed dropdown menus
  }

  loadVehicleTypes(page: number = 1, search: string = '', filters: Record<string, any> = {}): void {
    this.isLoading = true;
    this.currentPage = page;
    this.selectedVehicleTypes = []; // Clear selections when loading new data
    this.searchTerm = search;
    this.filters = filters;

    this.subscriptions.push(
      this._vehicleTypeService.GetData(page.toString(), search, filters).subscribe({
        next: (response: PaginatedResponse<any>) => {
          if (response.status && response.data) {
            this.vehicleTypes = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;
          } else {
            this.vehicleTypes = [];
            this.resetPagination();
          }
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars(); // Reset toolbar state
        },
        error: (error) => {
          console.error('Error loading vehicle types:', error);
          this.vehicleTypes = [];
          this.resetPagination();
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars(); // Reset toolbar state
        }
      })
    );
  }

  onPageChange(page: number): void {
    this.loadVehicleTypes(page, this.searchTerm, this.filters);
  }

  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  applyFilters(): void {
    this.loadVehicleTypes(1, this.searchTerm, this.filters);
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.filters = {};
    this.showFiltersForm = false;
    this.loadVehicleTypes();
  }

  openAddVehicleTypeModal(): void {
    this._vehicleTypeModalService.clearSelectedVehicleType();
    const modalElement = document.getElementById('kt_modal_vehicle_type_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openEditModal(vehicleType: any): void {
    this._vehicleTypeModalService.setSelectedVehicleType(vehicleType);
    const modalElement = document.getElementById('kt_modal_vehicle_type_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  deleteVehicleType(vehicleType: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete "${vehicleType.name}". This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDelete(vehicleType);
      }
    });
  }

  private performDelete(vehicleType: any): void {
    const vehicleTypeId = vehicleType._id || vehicleType.id;
    this.subscriptions.push(
      this._vehicleTypeService.DeleteDataById(vehicleTypeId).subscribe({
        next: (response: any) => {
          if (response.status) {
            Swal.fire({
              title: 'Deleted!',
              text: 'Vehicle type has been deleted successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.loadVehicleTypes(this.currentPage, this.searchTerm, this.filters);
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to delete vehicle type.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while deleting vehicle type.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  // Event handlers for modal events
  onVehicleTypeAdded(): void {
    console.log('Vehicle type added, refreshing list...');
    this.loadVehicleTypes(this.currentPage, this.searchTerm, this.filters);
  }

  onVehicleTypeUpdated(): void {
    console.log('Vehicle type updated, refreshing list...');
    this.loadVehicleTypes(this.currentPage, this.searchTerm, this.filters);
  }

  // Utility methods
  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 10,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }



  // Track by function for ngFor performance
  trackByFn(index: number, item: any): any {
    return item._id || index;
  }

  // Bulk selection methods
  isSelected(vehicleTypeId: string): boolean {
    return this.selectedVehicleTypes.includes(vehicleTypeId);
  }

  isAllSelected(): boolean {
    return this.vehicleTypes.length > 0 && this.selectedVehicleTypes.length === this.vehicleTypes.length;
  }

  isIndeterminate(): boolean {
    return this.selectedVehicleTypes.length > 0 && this.selectedVehicleTypes.length < this.vehicleTypes.length;
  }

  toggleSelection(vehicleTypeId: string, event: any): void {
    if (event.target.checked) {
      if (!this.selectedVehicleTypes.includes(vehicleTypeId)) {
        this.selectedVehicleTypes.push(vehicleTypeId);
      }
    } else {
      this.selectedVehicleTypes = this.selectedVehicleTypes.filter(id => id !== vehicleTypeId);
    }
    this.toggleToolbars();
  }

  toggleAllSelection(event: any): void {
    if (event.target.checked) {
      this.selectedVehicleTypes = this.vehicleTypes.map(vehicleType => vehicleType._id);
    } else {
      this.selectedVehicleTypes = [];
    }
    this.toggleToolbars();
  }

  private toggleToolbars(): void {
    const toolbarBase = document.querySelector('[data-kt-vehicle-type-table-toolbar="base"]');
    const toolbarSelected = document.querySelector('[data-kt-vehicle-type-table-toolbar="selected"]');

    if (this.selectedVehicleTypes.length > 0) {
      toolbarBase?.classList.add('d-none');
      toolbarSelected?.classList.remove('d-none');
    } else {
      toolbarBase?.classList.remove('d-none');
      toolbarSelected?.classList.add('d-none');
    }
  }

  deleteSelectedVehicleTypes(): void {
    if (this.selectedVehicleTypes.length === 0) {
      return;
    }

    Swal.fire({
      title: 'Are you sure?',
      text: `You want to delete ${this.selectedVehicleTypes.length} selected vehicle type(s)?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete them!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performBulkDelete();
      }
    });
  }

  private performBulkDelete(): void {

    if (this.selectedVehicleTypes.length === 0) {
      return;
    }

    this.subscriptions.push(
      this._vehicleTypeService.BulkDelete(this.selectedVehicleTypes).subscribe({
        next: (response) => {
          this.loadVehicleTypes();
          this.selectedVehicleTypes = [];
          this.toggleToolbars();
          Swal.fire('Deleted!', 'Selected vehicle types have been deleted.', 'success');
        },
        error: (error) => {
          console.error('Error deleting vehicle types:', error);
          Swal.fire('Error!', 'Failed to delete selected vehicle types.', 'error');
        }
      })
    );
  }

  // Math utility for template
  Math = Math;
}
